import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import Header from "../components/Header";
import BottomNavbar from "../components/BottomNavbar";
import "./css/AddPastWork.css";

const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
const VERSION = process.env.REACT_APP_VERSION;

const MAX_PHOTOS = 5;
const MAX_DOCUMENTS = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// Allowed image types for photos
const ALLOWED_IMAGE_TYPES = [
  "image/png",
  "image/jpg",
  "image/jpeg",
  "image/svg+xml",
  "image/webp",
  "image/avif",
  "image/heic",
  "image/heif",
];

// Allowed types for documents (images + PDF + DOC)
const ALLOWED_DOC_TYPES = [
  "image/png",
  "image/jpg",
  "image/jpeg",
  "image/svg+xml",
  "image/webp",
  "image/avif",
  "image/heic",
  "image/heif",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export default function AddPastWork() {
  const navigate = useNavigate();
  const [lang] = useState(localStorage.getItem("app_language") || "en");
  const [loading, setLoading] = useState(false);
  
  // Form fields
  const [title, setTitle] = useState("");
  const [budget, setBudget] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [completionDate, setCompletionDate] = useState("");
  
  // File uploads
  const [photos, setPhotos] = useState([]);
  const [documents, setDocuments] = useState([]);
  
  // Refs
  const photoInputRef = useRef(null);
  const documentInputRef = useRef(null);
  
  // Validation errors
  const [errors, setErrors] = useState({});

  // Translations
  const t = {
    en: {
      pastWorks: "Past Works",
      addPastWork: "Add Past Work",
      fillForm: "Fill the below form to add a new past work",
      title: "Title",
      titlePlaceholder: "Title of your project",
      photos: "Photos",
      photosInfo: "Max 5 photos allowed, upload png or jpg only",
      clickToUpload: "Click Here To Upload",
      budget: "Budget",
      workStartDate: "Work Start Date",
      workCompletionDate: "Work Completion Date",
      clickToSelect: "Click Here to Select",
      description: "Description",
      descriptionPlaceholder: "Add the work description here...",
      workDocuments: "Work Documents",
      documentsInfo: "Max 5 documents allowed, upload pdf or jpg only",
      addWork: "Add Work",
      loading: "Loading",
      invalidFileType: "Invalid file type",
      fileTooLarge: "File size must be less than 5MB",
      maxFilesReached: "Maximum files reached",
      atLeastOnePhoto: "At least one photo is required",
      titleRequired: "Title is required",
      titleMaxLength: "Title must be less than 100 characters",
      budgetRequired: "Budget is required",
      budgetInvalid: "Budget must be a valid number between 0 and 1,000,000,000",
      descriptionRequired: "Description is required",
      descriptionMaxLength: "Description must be less than 2000 characters",
      startDateRequired: "Work start date is required",
      completionDateRequired: "Work completion date is required",
      completionBeforeStart: "Completion date must be after start date",
      addSuccess: "Past work added successfully!",
      addFailed: "Failed to add past work",
    },
    ar: {
      pastWorks: "الأعمال السابقة",
      addPastWork: "إضافة عمل سابق",
      fillForm: "املأ النموذج أدناه لإضافة عمل سابق جديد",
      title: "العنوان",
      titlePlaceholder: "عنوان مشروعك",
      photos: "الصور",
      photosInfo: "الحد الأقصى 5 صور، قم بتحميل PNG أو JPG فقط",
      clickToUpload: "انقر هنا للتحميل",
      budget: "الميزانية",
      workStartDate: "تاريخ بدء العمل",
      workCompletionDate: "تاريخ إتمام العمل",
      clickToSelect: "انقر هنا للاختيار",
      description: "الوصف",
      descriptionPlaceholder: "أضف وصف العمل هنا...",
      workDocuments: "مستندات العمل",
      documentsInfo: "الحد الأقصى 5 مستندات، قم بتحميل PDF أو JPG فقط",
      addWork: "إضافة عمل",
      loading: "جاري التحميل",
      invalidFileType: "نوع ملف غير صحيح",
      fileTooLarge: "يجب أن يكون حجم الملف أقل من 5 ميجابايت",
      maxFilesReached: "تم الوصول إلى الحد الأقصى للملفات",
      atLeastOnePhoto: "مطلوب صورة واحدة على الأقل",
      titleRequired: "العنوان مطلوب",
      titleMaxLength: "يجب أن يكون العنوان أقل من 100 حرف",
      budgetRequired: "الميزانية مطلوبة",
      budgetInvalid: "يجب أن تكون الميزانية رقماً صحيحاً بين 0 و 1,000,000,000",
      descriptionRequired: "الوصف مطلوب",
      descriptionMaxLength: "يجب أن يكون الوصف أقل من 2000 حرف",
      startDateRequired: "تاريخ بدء العمل مطلوب",
      completionDateRequired: "تاريخ إتمام العمل مطلوب",
      completionBeforeStart: "يجب أن يكون تاريخ الإتمام بعد تاريخ البدء",
      addSuccess: "تمت إضافة العمل السابق بنجاح!",
      addFailed: "فشلت إضافة العمل السابق",
    },
  }[lang];

  // Validate file type for photos
  const validatePhotoFile = (file) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      const fileName = file.name.toLowerCase();
      const hasValidExtension = [".png", ".jpg", ".jpeg", ".svg", ".webp", ".avif", ".heic", ".heif"].some(
        (ext) => fileName.endsWith(ext)
      );
      if (!hasValidExtension) {
        return { valid: false, error: t.invalidFileType };
      }
    }
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: t.fileTooLarge };
    }
    return { valid: true };
  };

  // Validate file type for documents
  const validateDocumentFile = (file) => {
    if (!ALLOWED_DOC_TYPES.includes(file.type)) {
      const fileName = file.name.toLowerCase();
      const hasValidExtension = [".png", ".jpg", ".jpeg", ".svg", ".webp", ".avif", ".heic", ".pdf", ".doc", ".docx"].some(
        (ext) => fileName.endsWith(ext)
      );
      if (!hasValidExtension) {
        return { valid: false, error: t.invalidFileType };
      }
    }
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: t.fileTooLarge };
    }
    return { valid: true };
  };

  // Handle photo selection
  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > MAX_PHOTOS) {
      showToast(t.maxFilesReached, "error");
      return;
    }

    const validFiles = [];
    for (const file of files) {
      const validation = validatePhotoFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        showToast(validation.error, "error");
      }
    }

    if (validFiles.length > 0) {
      const newTotal = photos.length + validFiles.length;
      if (newTotal > MAX_PHOTOS) {
        const remaining = MAX_PHOTOS - photos.length;
        setPhotos([...photos, ...validFiles.slice(0, remaining)]);
        showToast(`Only ${remaining} more photo(s) can be uploaded.`, "error");
      } else {
        setPhotos([...photos, ...validFiles]);
      }
    }

    if (photoInputRef.current) {
      photoInputRef.current.value = "";
    }
  };

  // Handle document selection
  const handleDocumentSelect = (e) => {
    const files = Array.from(e.target.files);
    if (documents.length + files.length > MAX_DOCUMENTS) {
      showToast(t.maxFilesReached, "error");
      return;
    }

    const validFiles = [];
    for (const file of files) {
      const validation = validateDocumentFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        showToast(validation.error, "error");
      }
    }

    if (validFiles.length > 0) {
      const newTotal = documents.length + validFiles.length;
      if (newTotal > MAX_DOCUMENTS) {
        const remaining = MAX_DOCUMENTS - documents.length;
        setDocuments([...documents, ...validFiles.slice(0, remaining)]);
        showToast(`Only ${remaining} more document(s) can be uploaded.`, "error");
      } else {
        setDocuments([...documents, ...validFiles]);
      }
    }

    if (documentInputRef.current) {
      documentInputRef.current.value = "";
    }
  };

  // Remove photo
  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  // Remove document
  const removeDocument = (index) => {
    setDocuments(documents.filter((_, i) => i !== index));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Title validation
    if (!title.trim() || title.trim().length === 0) {
      newErrors.title = t.titleRequired;
    } else if (title.trim().length > 100) {
      newErrors.title = t.titleMaxLength;
    }

    // Budget validation
    const budgetStr = budget.toString().trim();
    if (!budgetStr || budgetStr === "" || budgetStr === ".") {
      newErrors.budget = t.budgetRequired;
    } else {
      const budgetNum = parseFloat(budgetStr);
      if (isNaN(budgetNum) || budgetNum < 0 || budgetNum > 1000000000) {
        newErrors.budget = t.budgetInvalid;
      }
    }

    // Description validation
    if (!description.trim() || description.trim().length === 0) {
      newErrors.description = t.descriptionRequired;
    } else if (description.trim().length > 2000) {
      newErrors.description = t.descriptionMaxLength;
    }

    // Start date validation
    if (!startDate) {
      newErrors.startDate = t.startDateRequired;
    }

    // Completion date validation
    if (!completionDate) {
      newErrors.completionDate = t.completionDateRequired;
    } else if (startDate && completionDate) {
      const start = new Date(startDate);
      const completion = new Date(completionDate);
      if (completion <= start) {
        newErrors.completionDate = t.completionBeforeStart;
      }
    }

    // At least one photo required
    if (photos.length === 0) {
      newErrors.photos = t.atLeastOnePhoto;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle budget change
  const handleBudgetChange = (e) => {
    const value = e.target.value;
    // Allow empty string, numbers, and decimals
    if (value === "" || value === "." || /^\d*\.?\d*$/.test(value)) {
      setBudget(value);
      if (errors.budget) setErrors({ ...errors, budget: null });
    }
  };

  // Get today's date in YYYY-MM-DD format for max date
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!validateForm()) {
      // Prevent scrolling to error field
      const firstErrorField = document.querySelector('.form-input.error, .form-textarea.error');
      if (firstErrorField) {
        firstErrorField.focus({ preventScroll: true });
      }
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();

      // Add form fields
      formData.append("title", title.trim());
      formData.append("budget", parseFloat(budget));
      formData.append("description", description.trim());
      formData.append("start_date", startDate);
      formData.append("completion_date", completionDate);

      // Add photos
      photos.forEach((photo) => {
        formData.append("photos", photo);
      });

      // Add documents
      documents.forEach((document) => {
        formData.append("documents", document);
      });

      const accessToken = localStorage.getItem("accessToken");
      const response = await fetch(`${USER_BASE_URL}/${VERSION}/past-works/add`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "app-version": "1.0.0",
        },
        body: formData,
      });

      const data = await response.json();

      if (data && data.response_code === 200) {
        showToast(t.addSuccess, "success");
        navigate("/past-works");
      } else {
        showToast(data.message || t.addFailed, "error");
      }
    } catch (err) {
      console.error("Error adding past work:", err);
      showToast(err.message || t.addFailed, "error");
    } finally {
      setLoading(false);
    }
  };

  // Get preview URL for photo
  const getPhotoPreview = (file) => {
    return URL.createObjectURL(file);
  };

  // Get file extension for document icon
  const getDocumentExtension = (file) => {
    const fileName = file.name.toLowerCase();
    if (fileName.endsWith(".pdf")) return "PDF";
    if (fileName.endsWith(".doc") || fileName.endsWith(".docx")) return "DOC";
    if (fileName.endsWith(".jpg") || fileName.endsWith(".jpeg")) return "JPG";
    if (fileName.endsWith(".png")) return "PNG";
    return "FILE";
  };

  return (
    <div className="add-past-work-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Header */}
      <Header lang={lang} />

      {/* Page Header */}
      <div className="add-past-work-page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <i className="bx bx-arrow-back"></i>
        </button>
        <h1 className="add-past-work-page-title">{t.pastWorks}</h1>
        <div className="header-spacer"></div>
      </div>

      {/* Main Content */}
      <div className="add-past-work-content">
        <h2 className="add-past-work-heading">{t.addPastWork}</h2>
        <p className="add-past-work-subtitle">{t.fillForm}</p>

        <form onSubmit={handleSubmit}>
          {/* Title */}
          <div className="form-field">
            <label className="form-label">{t.title}</label>
            <input
              type="text"
              className={`form-input ${errors.title ? "error" : ""}`}
              placeholder={t.titlePlaceholder}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (errors.title) setErrors({ ...errors, title: null });
              }}
              maxLength={100}
            />
            {errors.title && <span className="error-message">{errors.title}</span>}
          </div>

          {/* Photos */}
          <div className="form-field">
            <label className="form-label">{t.photos}</label>
            <p className="form-info">{t.photosInfo}</p>
            <div className="file-upload-section">
              <input
                ref={photoInputRef}
                type="file"
                multiple
                accept=".png,.jpg,.jpeg,.svg,.webp,.avif,.heic,.heif"
                onChange={handlePhotoSelect}
                className="file-input-hidden"
                disabled={photos.length >= MAX_PHOTOS}
              />
              
              {/* Photo Previews */}
              {photos.length > 0 && (
                <div className="photo-grid">
                  {photos.map((photo, index) => (
                    <div key={index} className="photo-item">
                      <img
                        src={getPhotoPreview(photo)}
                        alt={`${index + 1}`}
                        className="photo-preview"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="photo-delete-btn"
                        aria-label="Delete photo"
                      >
                        <i className="bx bx-trash"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Area */}
              {photos.length < MAX_PHOTOS && (
                <div
                  onClick={() => photoInputRef.current?.click()}
                  className="file-upload-area"
                >
                  <p>{t.clickToUpload}</p>
                </div>
              )}
            </div>
            {errors.photos && <span className="error-message">{errors.photos}</span>}
          </div>

          {/* Budget */}
          <div className="form-field">
            <label className="form-label">{t.budget}</label>
            <input
              type="number"
              className={`form-input ${errors.budget ? "error" : ""}`}
              placeholder="0"
              value={budget}
              onChange={handleBudgetChange}
              min="0"
              max="1000000000"
              step="0.01"
            />
            {errors.budget && <span className="error-message">{errors.budget}</span>}
          </div>

          {/* Work Start Date */}
          <div className="form-field">
            <label className="form-label">{t.workStartDate}</label>
            <div className="date-input-wrapper">
              <input
                type="date"
                className={`form-input date-input ${errors.startDate ? "error" : ""}`}
                placeholder={t.clickToSelect}
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (errors.startDate) setErrors({ ...errors, startDate: null });
                }}
                max={completionDate ? (completionDate < getTodayDate() ? completionDate : getTodayDate()) : getTodayDate()}
              />
              <i className="bx bx-calendar date-icon"></i>
            </div>
            {errors.startDate && <span className="error-message">{errors.startDate}</span>}
          </div>

          {/* Work Completion Date */}
          <div className="form-field">
            <label className="form-label">{t.workCompletionDate}</label>
            <div className="date-input-wrapper">
              <input
                type="date"
                className={`form-input date-input ${errors.completionDate ? "error" : ""}`}
                placeholder={t.clickToSelect}
                value={completionDate}
                onChange={(e) => {
                  setCompletionDate(e.target.value);
                  if (errors.completionDate) setErrors({ ...errors, completionDate: null });
                }}
                min={startDate || undefined}
                max={getTodayDate()}
              />
              <i className="bx bx-calendar date-icon"></i>
            </div>
            {errors.completionDate && <span className="error-message">{errors.completionDate}</span>}
          </div>

          {/* Description */}
          <div className="form-field">
            <label className="form-label">{t.description}</label>
            <textarea
              className={`form-textarea ${errors.description ? "error" : ""}`}
              placeholder={t.descriptionPlaceholder}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                if (errors.description) setErrors({ ...errors, description: null });
              }}
              maxLength={2000}
              rows={4}
            />
            {errors.description && <span className="error-message">{errors.description}</span>}
          </div>

          {/* Work Documents */}
          <div className="form-field">
            <label className="form-label">{t.workDocuments}</label>
            <p className="form-info">{t.documentsInfo}</p>
            <div className="file-upload-section">
              <input
                ref={documentInputRef}
                type="file"
                multiple
                accept=".png,.jpg,.jpeg,.svg,.webp,.avif,.heic,.pdf,.doc,.docx"
                onChange={handleDocumentSelect}
                className="file-input-hidden"
                disabled={documents.length >= MAX_DOCUMENTS}
              />
              
              {/* Document Previews */}
              {documents.length > 0 && (
                <div className="document-grid">
                  {documents.map((document, index) => (
                    <div key={index} className="document-item">
                      <div className="document-icon">
                        {getDocumentExtension(document)}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeDocument(index)}
                        className="document-delete-btn"
                        aria-label="Delete document"
                      >
                        <i className="bx bx-x"></i>
                      </button>
                      <p className="document-name">
                        {document.name.length > 15
                          ? document.name.substring(0, 12) + "..."
                          : document.name}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Area */}
              {documents.length < MAX_DOCUMENTS && (
                <div
                  onClick={() => documentInputRef.current?.click()}
                  className="file-upload-area"
                >
                  <p>{t.clickToUpload}</p>
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="add-work-btn"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="button-spinner"></div>
                {t.loading}
              </>
            ) : (
              t.addWork
            )}
          </button>
        </form>
      </div>

      {/* Bottom Navigation */}
      <BottomNavbar currentPage="settings" lang={lang} />
    </div>
  );
}

